// Locations (SA-5) — porté de la maquette Super Admin v2 (bloc `isRent`) : suivi du parc en LOCATION.
// Échéancier Gantt sur 10 jours (barres réelles depuis rental_start/rental_end + segments de TRANSIT
// hachurés : l'appareil est immobilisé 2 j OUVRÉS avant/après les dates louées — règle d'affichage de la
// doctrine, pas une donnée), disponibilité PRÉVISIONNELLE (étiquetée « théorique » : disponibles
// aujourd'hui + retours attendus dans la fenêtre — il n'existe PAS de système de réservation future,
// on ne prédit rien d'autre), filtres + table + « Retour au parc » (action réelle).
// ⚠️ §23.0 : tout vient de `device` (régime, dates, magasin/client) ; la FACTURATION reste hors Console
// (Sliderz Business, SA-5.5) — ici c'est du suivi. « Nouvelle location » réutilise la modale d'affectation
// de la Flotte (règle de 2 — jamais recopiée).
import { dateCompact, weekday } from '@orgazone/date-format';
import { useEffect, useId, useMemo, useState } from 'react';
import { api, type DeviceRow, type StoreRow } from '../api';
import { DeviceName } from '../device-name';
import { alert, confirm, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { AssignDevice } from './Fleet';
import { EmptyState } from '@orgazone/ui-web';

const DAY = 86400000;
import { Pill } from '../ui';
import { TableScroll } from '../table-scroll';
import { addBusinessDays } from '../rental-days';
const fmtDay = dateCompact; // « 04/08/26 »
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

type Row = DeviceRow & { start: Date | null; end: Date | null; overdue: boolean; daysLeft: number | null };

export function Locations() {
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [stores, setStores] = useState<StoreRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'courte' | 'longue' | 'late' | 'soon'>('all');
  const [win, setWin] = useState<7 | 14 | 30>(7);
  const [picking, setPicking] = useState(false);          // « Nouvelle location » : choix de l'appareil
  const [assigning, setAssigning] = useState<DeviceRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef: pickPanel } = useOverlayPanel({ open: picking, onClose: () => setPicking(false) });
  const pickTitle = useId();

  const load = async () => {
    try {
      const [d, s] = await Promise.all([api.devices(), api.stores()]);
      setDevices(d.devices); setStores(s.stores); setErr(null);
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const today = startOfDay(new Date());
  // Les LOCATIONS = appareils affectés en régime courte/longue (l'achat n'est pas une location).
  const rentals: Row[] = useMemo(() => (devices ?? [])
    .filter((d) => d.status === 'assigned' && (d.rentalType === 'courte' || d.rentalType === 'longue'))
    .map((d) => {
      const start = d.rentalStart ? startOfDay(new Date(d.rentalStart)) : null;
      const end = d.rentalEnd ? startOfDay(new Date(d.rentalEnd)) : null;
      const daysLeft = end ? Math.ceil((end.getTime() - today.getTime()) / DAY) : null;
      return { ...d, start, end, overdue: daysLeft !== null && daysLeft < 0, daysLeft };
    })
    .sort((a, b) => (a.end?.getTime() ?? Infinity) - (b.end?.getTime() ?? Infinity)), [devices, today]);

  const late = rentals.filter((r) => r.overdue);
  const shown = rentals.filter((r) =>
    filter === 'all' ? true
    : filter === 'late' ? r.overdue
    : filter === 'soon' ? !r.overdue && r.daysLeft !== null && r.daysLeft <= 30
    : r.rentalType === filter);

  // ── Échéancier — FIDÈLE au générateur `rentGantt` de la maquette : 10 jours À PARTIR D'AUJOURD'HUI,
  // BARRES CONTINUES (spans grid-column) avec étiquette de durée, segments de TRANSIT hachurés en pointillés
  // ACCOLÉS à la barre (2 j ouvrés avant/après), COULEUR PAR FAMILLE (Mobile Partner = in/bleu, Sliderz = vi/violet)
  // et retard = da/rouge. Une location en retard reste visible quelle que soit sa famille (barre jusqu'à
  // aujourd'hui : l'appareil est TOUJOURS dehors — fait réel, pas décor). ──
  const days = Array.from({ length: 10 }, (_, i) => { const d = new Date(today); d.setDate(d.getDate() + i); return d; });
  const dayIndex = (d: Date) => Math.round((startOfDay(d).getTime() - today.getTime()) / DAY);
  const HATCH = 'repeating-linear-gradient(45deg, var(--ink200) 0 3px, transparent 3px 6px)';
  interface GanttRow {
    r: Row;
    kind: 'in' | 'vi' | 'da';
    from: number; to: number;               // indices 0..9 clampés (barre)
    preF: number | null; postT: number | null; // transit clampé (null = hors fenêtre)
    label: string;
  }
  const ganttRows: GanttRow[] = rentals.flatMap((r): GanttRow[] => {
    if (!r.start) return [];
    if (!r.overdue && r.rentalType !== 'courte') return []; // l'échéancier = courtes + tout retard (maquette)
    const a = dayIndex(r.start);
    const b = r.end ? dayIndex(r.end) : 9; // sans échéance : la barre court jusqu'au bord (réalité : dehors)
    if (r.overdue) {
      // Toujours dehors après l'échéance : barre rouge du début clampé jusqu'à AUJOURD'HUI.
      return [{ r, kind: 'da', from: Math.max(0, a), to: 0, preF: null, postT: null, label: `retard ${Math.abs(r.daysLeft ?? 0)} j` }];
    }
    const pa = dayIndex(addBusinessDays(r.start, -2));
    const pb = r.end ? dayIndex(addBusinessDays(r.end, 2)) : null;
    if ((pb ?? 9) < 0 || pa > 9) return []; // entièrement hors fenêtre
    const from = Math.max(0, a), to = Math.min(9, b);
    if (to < from) return []; // seule la queue de transit serait visible — illisible, la table la porte
    const preF = pa < a && pa <= 9 ? Math.max(0, pa) : null;
    const postT = pb !== null && pb > b && pb >= 0 ? Math.min(9, pb) : null;
    const kind: GanttRow['kind'] = r.nature === 'mobile-partner' ? 'in' : 'vi';
    return [{ r, kind, from, to, preF: preF !== null && preF < from ? preF : null, postT: postT !== null && postT > to ? postT : null, label: `${b - a + 1} j` }];
  }).slice(0, 8); // lisibilité : 8 lignes max, le reste vit dans la table

  // ── Disponibilité prévisionnelle (THÉORIQUE — pas de réservations futures en base, on ne prédit que
  // depuis les retours attendus) : disponibles aujourd'hui par famille + retours dans la fenêtre. ──
  const winEnd = new Date(today.getTime() + win * DAY);
  const availNow = (devices ?? []).filter((d) => d.status === 'available');
  // ⚠️ Par la NATURE du type : un poste, un TPE ou un appareil sans type ne se comptent pas comme une caisse disponible.
  const availByFam = ([['mobile-partner', 'Mobile Partner'], ['caisse', 'Caisses & tablettes']] as const)
    .map(([nature, fam]) => ({ fam, n: availNow.filter((d) => d.nature === nature).length }));
  const freeing = rentals.filter((r) => r.end && !r.overdue && addBusinessDays(r.end, 2) <= winEnd);

  const returnToPool = async (r: Row) => {
    const ok = await confirm({
      title: `Retour au parc — ${r.model ?? r.family} ?`,
      message: `Fin de la location ${r.rentalType} chez ${r.storeName ?? '—'}${r.registerNumber != null ? ` (caisse n° ${r.registerNumber} — le numéro ne sera JAMAIS réutilisé)` : ''}. L'appareil redevient disponible.`,
      confirmLabel: 'Retour au parc', tone: 'info',
    });
    if (!ok) return;
    setBusyId(r.id);
    try { await api.deviceAction(r.id, { action: 'unassign' }); showToast(`${r.model ?? r.family} rendu au parc.`); await load(); }
    catch (e) { void alert({ title: 'Échec du retour au parc', message: (e as Error).message }); }
    finally { setBusyId(null); }
  };

  const FILTERS: { k: typeof filter; label: string; n?: number }[] = [
    { k: 'all', label: 'Toutes', n: rentals.length }, { k: 'courte', label: 'Courte durée' }, { k: 'longue', label: 'Longue durée' },
    { k: 'late', label: 'En retard', n: late.length }, { k: 'soon', label: 'Se termine < 30 j' },
  ];

  return (
    <div className="page">
      {picking && (
        <div className="modalwrap" onClick={() => setPicking(false)}>
          <div className="modal" ref={pickPanel} role="dialog" aria-modal="true" aria-labelledby={pickTitle}
            onClick={(e) => e.stopPropagation()}>
            <h3 id={pickTitle}>Nouvelle location — choisir l'appareil</h3>
            {availNow.length === 0 && <EmptyState compact icon="tablet" title="Aucun appareil disponible" note="Tout le parc est affecté — le retour d'une location en libérera, ou ajoutez un appareil depuis le registre de la flotte." />}
            {availNow.map((d) => (
              <button key={d.id} onClick={() => { setPicking(false); setAssigning(d); }}
                className="row-pick" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', marginTop: 6, borderRadius: 'var(--r-sm)', background: 'transparent', color: 'var(--ink900)', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, flex: 1 }}><DeviceName d={d} /></span>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{d.family}{d.model ? ` · ${d.model}` : ''}{d.serial ? ` · ${d.serial}` : ''}</span>
              </button>
            ))}
            <div className="modalfoot"><button className="ghost btn-ghost" style={{ width: 'auto', padding: '8px 14px' }} onClick={() => setPicking(false)}>Annuler</button></div>
          </div>
        </div>
      )}
      {assigning && (
        <AssignDevice dev={assigning} stores={stores} onClose={() => setAssigning(null)}
          onDone={(msg) => { setAssigning(null); showToast(msg); void load(); }} />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Locations</h1>
          {/* Sans contrainte de mesure — cf. Clients/Blocages : seule la reprise en porte une dans la maquette. */}
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.5 }}>
            Locations du parc Mobile Partner &amp; Sliderz — les retours en retard y figurent quelle que soit la famille.
            Chaque location immobilise l'appareil <b style={{ color: 'var(--ink700)' }}>2 jours ouvrés</b> avant et après les dates louées (transit aller-retour).
            La facturation vit dans Sliderz Business — ici, le suivi.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setPicking(true)} className="btn-cta" style={{ height: 36, padding: '0 16px', borderRadius: 'var(--r-sm)', fontSize: 'var(--t-body)' }}>
          Nouvelle location
        </button>
      </div>

      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      {!devices && !err && <div className="muted" style={{ padding: 16 }}>Chargement du parc…</div>}

      {devices && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 14, marginBottom: 14, alignItems: 'start' }}>
          {/* ── Échéancier (Gantt réel) ── */}
          <div className="dcard" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Échéancier</span>
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{fmtDay(days[0]!.toISOString())} → {fmtDay(days[9]!.toISOString())}</span>
              <div style={{ flex: 1 }} />
              {/* Légende maquette : par FAMILLE + transit. */}
              {([
                ['Mobile Partner', 'var(--in-bg)', 'var(--chart-in)'],
                ['Sliderz', 'var(--vi-bg)', 'var(--chart-vi)'],
                ['transit (2 j ouvrés)', HATCH, 'var(--ink300)'],
              ] as const).map(([l, bg, bd]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 12, height: 8, borderRadius: 'var(--r-r3)', background: bg, border: `1px solid ${bd}` }} />
                  <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink600)' }}>{l}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '14px 18px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '118px repeat(10, minmax(0,1fr))', gap: 4, alignItems: 'center', marginBottom: 8 }}>
                <div />
                {days.map((d, i) => (
                  <div key={i} style={{ textAlign: 'center', color: i === 0 ? 'var(--teal)' : 'var(--ink500)' }}>
                    <div style={{ fontSize: 'var(--t-eyebrow)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{weekday(d)}</div>
                    <div className="mono" style={{ fontSize: 'var(--t-note)', fontWeight: 600, marginTop: 1 }}>{d.getDate()}</div>
                  </div>
                ))}
              </div>
              {ganttRows.length === 0 && <EmptyState compact icon="calendar" title="Aucune location courte dans la fenêtre" note="L'échéancier montre la rotation du parc sur 10 jours — il se remplira dès la première location datée (les retards y apparaissent toujours)." />}
              {ganttRows.map((g) => {
                const fg = g.kind === 'in' ? 'var(--in-fg)' : g.kind === 'vi' ? 'var(--vi-fg)' : 'var(--da-fg)';
                return (
                  <div key={g.r.id} style={{ display: 'grid', gridTemplateColumns: '118px repeat(10, minmax(0,1fr))', gap: 4, alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="mono" style={{ fontSize: 'var(--t-meta)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.r.model ?? g.r.family}</div>
                      <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.r.tenantName ?? '—'} · {g.r.storeName ?? '—'}</div>
                    </div>
                    {/* Transit ALLER (hachuré pointillé, accolé à gauche de la barre). */}
                    {g.preF !== null && (
                      <div style={{ gridColumn: `${g.preF + 2} / ${g.from + 2}`, height: 24, borderRadius: '6px 0 0 6px', background: HATCH, border: '1px dashed var(--ink300)', borderRight: 'none' }} />
                    )}
                    {/* La BARRE de location : span continu, fond teinté famille, étiquette de durée. */}
                    <div style={{ gridColumn: `${g.from + 2} / ${g.to + 3}`, height: 24, borderRadius: 'var(--r-xs)', background: `var(--${g.kind}-bg)`, border: `1px solid var(--chart-${g.kind})`, display: 'flex', alignItems: 'center', padding: '0 8px', minWidth: 0 }}>
                      <span style={{ fontSize: 'var(--t-sub)', fontWeight: 600, color: fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.label}</span>
                    </div>
                    {/* Transit RETOUR (accolé à droite). */}
                    {g.postT !== null && (
                      <div style={{ gridColumn: `${g.to + 3} / ${g.postT + 3}`, height: 24, borderRadius: '0 6px 6px 0', background: HATCH, border: '1px dashed var(--ink300)', borderLeft: 'none' }} />
                    )}
                  </div>
                );
              })}
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--ink100)' }}>
                Les segments hachurés sont le transit : l'appareil est immobilisé mais pas chez le client.
              </div>
            </div>
          </div>

          {/* ── Disponibilité prévisionnelle (théorique) ── */}
          <div className="dcard" style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Disponibilité prévisionnelle</span>
              <Pill bg="var(--wa-bg)" fg="var(--wa-fg)">théorique</Pill>
            </div>
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 5, lineHeight: 1.5 }}>
              Si chaque appareil revient à l'heure, transit de 2 j ouvrés compris. Un retard décale tout.
            </div>
            <div style={{ display: 'flex', background: 'var(--canvas)', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', padding: 3, gap: 2, marginTop: 12 }}>
              {([7, 14, 30] as const).map((w) => (
                <button aria-pressed={win === w} key={w} onClick={() => setWin(w)}
                  style={{ flex: 1, height: 26, borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-note)', fontWeight: 600, background: win === w ? 'var(--ink200)' : 'transparent', color: win === w ? 'var(--ink900)' : 'var(--ink500)' }}>
                  {w} jours
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
              {availByFam.map((f) => (
                <div key={f.fam} style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', padding: '12px 14px', background: 'var(--ink100)' }}>
                  <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{f.fam}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 7 }}>
                    <span style={{ fontSize: 'var(--t-feature)', fontWeight: 800, color: f.n > 0 ? 'var(--ok-fg)' : 'var(--wa-fg)' }}>{f.n}</span>
                    <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>dispo. aujourd'hui</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 13 }}>
              <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)', marginBottom: 7 }}>se libèrent pendant la fenêtre</div>
              {freeing.length === 0 && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', padding: '6px 0' }}>Aucun retour prévu sur cette fenêtre.</div>}
              {freeing.map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', borderBottom: '1px solid var(--ink100)' }}>
                  <span className="mono" style={{ fontSize: 'var(--t-note)', fontWeight: 600 }}>{r.model ?? r.family}</span>
                  <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', flex: 1 }}>{r.tenantName ?? r.storeName ?? '—'}</span>
                  <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink600)' }}>{fmtDay(r.rentalEnd)} + transit</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Filtres + table ── */}
        <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <button aria-pressed={filter === f.k} key={f.k} onClick={() => setFilter(f.k)}
              className={filter === f.k ? 'chip info active' : 'chip info'} style={{ height: 26, padding: '0 10px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)' }}>
              {f.label}{f.n !== undefined ? ` (${f.n})` : ''}
            </button>
          ))}
        </div>
        <div className="dcard" style={{ overflow: 'hidden' }}>
          {shown.length === 0 ? <EmptyState icon="box" title="Aucune location sur ce filtre" note={rentals.length === 0 ? 'Aucun appareil en location — « Nouvelle location » affecte un appareil disponible du parc à un magasin.' : 'Changez de filtre pour voir les autres locations en cours.'} /> : (
            <TableScroll minWidth={700}>
              <thead><tr><th>Appareil</th><th>Client · magasin</th><th>Régime</th><th>Début</th><th>Fin</th><th>État</th><th></th></tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="mono" style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{r.model ?? r.family}{r.registerNumber != null ? ` · n° ${r.registerNumber}` : ''}</div>
                      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{r.family}{r.serial ? ` · ${r.serial}` : ''}</div>
                    </td>
                    <td style={{ fontSize: 'var(--t-body)' }}>{r.tenantName ?? '—'}<div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{r.storeName ?? '—'}</div></td>
                    <td><Pill bg={r.rentalType === 'courte' ? 'var(--in-bg)' : 'var(--vi-bg)'} fg={r.rentalType === 'courte' ? 'var(--in-fg)' : 'var(--vi-fg)'}>{r.rentalType === 'courte' ? 'Courte' : 'Longue'}</Pill></td>
                    <td className="mono" style={{ fontSize: 'var(--t-note)' }}>{fmtDay(r.rentalStart)}</td>
                    <td className="mono" style={{ fontSize: 'var(--t-note)' }}>{fmtDay(r.rentalEnd)}</td>
                    <td>
                      {r.overdue ? <Pill bg="var(--da-bg)" fg="var(--da-fg)">retard {Math.abs(r.daysLeft!)} j</Pill>
                        : r.daysLeft !== null ? <Pill bg={r.daysLeft <= 7 ? 'var(--wa-bg)' : 'var(--ok-bg)'} fg={r.daysLeft <= 7 ? 'var(--wa-fg)' : 'var(--ok-fg)'}>{r.daysLeft === 0 ? 'dernier jour' : `J−${r.daysLeft}`}</Pill>
                        : <Pill bg="var(--ne-bg)" fg="var(--ne-fg)">sans échéance</Pill>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="ghost btn-ghost" style={{ width: 'auto', padding: '4px 10px' }} disabled={busyId === r.id} onClick={() => void returnToPool(r)}>
                        {busyId === r.id ? '…' : 'Retour au parc'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}
        </div>
      </>}
    </div>
  );
}
